import SockJS from 'sockjs-client';
import { Client, IMessage } from '@stomp/stompjs';
import { getAccessToken, refreshToken } from './authService';
import config from '../config/config';

const API_BASE_URL = `${config.apiUrl}/chat`;
let WS_BASE_URL = config.wsUrl;
const USE_WEBSOCKETS = true; // Enable WebSockets for real-time updates

// Helper function for authenticated fetch requests
const authFetch = async (url: string, options: RequestInit = {}) => {
  try {
    const token = getAccessToken();
    
    if (!token) {
      console.error('No token available for chat API request');
      throw new Error('No authentication token available');
    }
    
    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
      'Content-Type': options.headers?.['Content-Type'] || 'application/json'
    };
    
    console.log(`ChatService: Making ${options.method || 'GET'} request to ${url}`);
    
    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers,
      credentials: 'include' // Include cookies for CSRF
    });
    
    // If unauthorized, try to refresh token once
    if (response.status === 401) {
      console.log('ChatService: Received 401, attempting token refresh');
      try {
        const newToken = await refreshToken();
        if (newToken) {
          console.log('ChatService: Token refreshed, retrying request');
          
          // Update headers with new token
          const newHeaders = {
            ...options.headers,
            'Authorization': `Bearer ${newToken}`,
            'Content-Type': options.headers?.['Content-Type'] || 'application/json'
          };
          
          // Retry the request with new token
          return fetch(`${API_BASE_URL}${url}`, {
            ...options,
            headers: newHeaders,
            credentials: 'include'
          });
        } else {
          console.error('ChatService: Token refresh failed');
          throw new Error('Authentication failed');
        }
      } catch (refreshError) {
        console.error('ChatService: Error refreshing token:', refreshError);
        throw new Error('Authentication failed');
      }
    }
    
    return response;
  } catch (error) {
    console.error(`ChatService: Error in authFetch for ${url}:`, error);
    throw error;
  }
};

export interface MediaItem {
  id: string;
  url: string;
  contentType?: string; // Make contentType optional
  fileName?: string;   // Make fileName optional
  size?: number;       // Make size optional
}

export interface ChatMessage {
  id: string;
  chatRoomId: string;
  senderId: string;
  senderName?: string;
  senderProfilePic?: string;
  content: string;
  timestamp: string;
  read: boolean;
  media?: MediaItem[];
  status?: 'sending' | 'sent'; // Add status property
}

export interface ChatRoom {
  id: string;
  participants: ChatParticipant[];
  lastMessageAt: string | null;
  lastMessageContent: string | null;
  lastMessageSenderId: string | null;
  unreadCount: number;
}

export interface ChatParticipant {
  id: string;
  fullName: string | null;
  email: string;
  profilePicture?: string;
  isOnline?: boolean;
}

// Add a helper function to format participant display name properly
export const formatParticipantName = (participant: ChatParticipant): string => {
  if (!participant) return "Unknown User";
  
  // If fullName exists and is not "null null", use it
  if (participant.fullName && 
      participant.fullName.trim() !== "" && 
      participant.fullName !== "null null" && 
      !participant.fullName.includes("null")) {
    return participant.fullName;
  }
  
  // Otherwise, use email (possibly extract username part)
  if (participant.email) {
    // Option 1: Use full email
    return participant.email;
    
    // Option 2: Extract username from email if preferred
    // const username = participant.email.split('@')[0];
    // return username.charAt(0).toUpperCase() + username.slice(1);
  }
  
  // Fallback
  return "Unknown User";
};

export interface ChatMessageRequest {
  chatRoomId: string;
  content: string;
  mediaIds?: string[];
}

// Singleton pattern for the STOMP client
class StompClientSingleton {
  private static _instance: StompClientSingleton | null = null;
  private static _client: Client | null = null;
  private messageCallbacks: Map<string, ((message: ChatMessage) => void)[]> = new Map();
  private connectedCallback: (() => void) | null = null;
  private reconnectIntervalId: NodeJS.Timeout | null = null;
  private roomTopicSubscriptions: Map<string, any> = new Map();
  
  private constructor() {}
  
  public static getInstance(): StompClientSingleton {
    if (!StompClientSingleton._instance) {
      StompClientSingleton._instance = new StompClientSingleton();
    }
    return StompClientSingleton._instance;
  }
  
  public getClient(): Client | null {
    return StompClientSingleton._client;
  }
  
  public connect(token: string, onConnected?: () => void): Client {
    if (StompClientSingleton._client && StompClientSingleton._client.connected) {
      console.log('Already connected, reusing connection');
      if (onConnected) {
        setTimeout(onConnected, 0);
      }
      return StompClientSingleton._client;
    }
    
    this.connectedCallback = onConnected || null;
    
    // Get a fresh token to ensure it's not expired
    const freshToken = getAccessToken();
    
    const tokenExists = freshToken !== null;
    console.log('Connecting with token:', tokenExists ? 'Token exists' : 'No token');
    
    //if the wsUrl starts with VITE_WS_URL= remove it
    if (WS_BASE_URL.startsWith('VITE_WS_URL=')) {
      WS_BASE_URL = WS_BASE_URL.substring('VITE_WS_URL='.length);
    }
    console.log('Connecting to WebSocket URL:', WS_BASE_URL);
    
    const client = new Client({
      webSocketFactory: () => new SockJS(`${WS_BASE_URL}`),
      connectHeaders: {
        'Authorization': `Bearer ${freshToken}`
      },
      debug: function(str) {
        console.log('STOMP Debug:', str); // Enable for debugging
      },
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000
    });
    
    client.onConnect = this.onConnect.bind(this);
    client.onStompError = this.onStompError.bind(this);
    
    client.activate();
    StompClientSingleton._client = client;
    
    return client;
  }
  
  private onConnect(frame: any) {
    console.log('🟢 Connected to WebSocket', frame);
    const client = StompClientSingleton._client;
    
    if (client) {
      // Get the user ID from localStorage
      const userId = localStorage.getItem('userId');
      console.log('🟢 User ID for subscription:', userId);
      
      if (userId) {
        // Subscribe to the user's queue for private messages
        client.subscribe(`/user/${userId}/queue/messages`, this.onMessageReceived.bind(this));
        console.log(`🟢 Subscribed to /user/${userId}/queue/messages`);
        
        // Also subscribe to the public topic for debugging
        client.subscribe('/topic/public', (message) => {
          console.log('🟢 Received message on public topic:', message);
        });
      } else {
        console.error('❌ No user ID found in localStorage, cannot subscribe to messages');
      }
      
      // Call the connected callback if provided
      if (this.connectedCallback) {
        this.connectedCallback();
      }
    }
  }
  
  private onStompError(frame: any) {
    console.error('STOMP error', frame);
    
    // Check if it's an authentication error
    if (frame.headers && frame.headers.message && 
        frame.headers.message.includes('Failed to send message')) {
      console.log('Authentication error with WebSocket, will retry with fresh token');
      
      // Disconnect and reconnect with fresh token
      if (StompClientSingleton._client) {
        StompClientSingleton._client.deactivate();
        StompClientSingleton._client = null;
        
        // Reconnect after a short delay
        setTimeout(() => {
          this.connect(getAccessToken(), this.connectedCallback || undefined);
        }, 1000);
      }
    }
  }
  
  private onMessageReceived(message: IMessage) {
    console.log('Raw WebSocket message received:', message);
    
    try {
      const rawMessage = JSON.parse(message.body);
      console.log('Parsed raw message:', rawMessage);
      
      // Convert using the shared method
      const chatMessage = this.convertMessageFormat(rawMessage);
      
      console.log('Converted chat message with media:', chatMessage);
      
      // Notify ALL callbacks for this chat room
      const chatRoomId = chatMessage.chatRoomId;
      console.log('Notifying callbacks for room:', chatRoomId);
      
      if (this.messageCallbacks.has(chatRoomId)) {
        const callbacks = this.messageCallbacks.get(chatRoomId) || [];
        console.log(`Found ${callbacks.length} callbacks for room ${chatRoomId}`);
        
        callbacks.forEach(callback => {
          console.log('Executing callback for message:', chatMessage.id);
          callback(chatMessage);
        });
      } else {
        console.warn(`No callbacks registered for room ${chatRoomId}`);
      }
    } catch (error) {
      console.error('Error processing message:', error);
    }
  }
  
  public subscribeToRoom(chatRoomId: string, callback: (message: ChatMessage) => void) {
    console.log(`🔔 Subscribing to room ${chatRoomId}`);
    
    // Make sure WebSocket is connected
    const client = this.getClient();
    if (!client || !client.connected) {
      console.log('❗ WebSocket not connected, connecting now...');
      const token = getAccessToken();
      this.connect(token, () => {
        console.log('🔔 Connected, now subscribing to room');
        this.registerRoomCallback(chatRoomId, callback);
        
        // Also subscribe to the chat room topic directly
        if (client) {
          const subscription = client.subscribe(`/topic/chat/${chatRoomId}`, (message) => {
            try {
              const messageData = JSON.parse(message.body);
              console.log('🔔 Received message via room topic:', messageData);
              
              // Convert to our format and call the callback
              const chatMessage = this.convertMessageFormat(messageData);
              callback(chatMessage);
            } catch (error) {
              console.error('❌ Error processing topic message:', error);
            }
          });
          
          // Store this subscription
          this.roomTopicSubscriptions.set(chatRoomId, subscription);
        }
      });
      return;
    }
    
    // Subscribe via the singleton
    this.registerRoomCallback(chatRoomId, callback);
    
    // Also subscribe to the chat room topic directly
    if (client) {
      const subscription = client.subscribe(`/topic/chat/${chatRoomId}`, (message) => {
        try {
          const messageData = JSON.parse(message.body);
          console.log('🔔 Received message via room topic:', messageData);
          
          // Convert to our format and call the callback
          const chatMessage = this.convertMessageFormat(messageData);
          callback(chatMessage);
        } catch (error) {
          console.error('❌ Error processing topic message:', error);
        }
      });
      
      // Store this subscription
      this.roomTopicSubscriptions.set(chatRoomId, subscription);
    }
  }
  
  public unsubscribeFromRoom(chatRoomId: string, callback: (message: ChatMessage) => void) {
    if (this.messageCallbacks.has(chatRoomId)) {
      const callbacks = this.messageCallbacks.get(chatRoomId) || [];
      const index = callbacks.indexOf(callback);
      
      if (index !== -1) {
        callbacks.splice(index, 1);
        this.messageCallbacks.set(chatRoomId, callbacks);
      }
    }
  }
  
  public disconnect() {
    if (StompClientSingleton._client) {
      StompClientSingleton._client.deactivate();
      StompClientSingleton._client = null;
    }
  }
  
  // Add this function to convert backend message format to frontend format
  public convertMessageFormat(rawMessage: any): ChatMessage {
    // Get timestamp from the server response
    let timestamp = rawMessage.sentAt || rawMessage.timestamp;
    
    // If timestamp is provided but doesn't include timezone information, add explicit UTC marker
    // This is crucial for consistent interpretation across environments
    if (timestamp && typeof timestamp === 'string') {
      const originalTimestamp = timestamp;
      
      // String has ISO format (contains 'T') but no timezone marker
      if (timestamp.includes('T') && !timestamp.includes('Z') && !timestamp.includes('+') && !timestamp.includes('-')) {
        timestamp = `${timestamp}Z`; // Mark as UTC
        console.log('ChatService: Added UTC marker to timestamp:', originalTimestamp, '->', timestamp);
      }
      
      // Debug log the timestamp
      try {
        const date = new Date(timestamp);
        console.log('ChatService: Processed timestamp:', {
          original: originalTimestamp,
          processed: timestamp,
          parsed: date.toISOString(),
          localTime: date.toLocaleString()
        });
      } catch (e) {
        console.error('ChatService: Error parsing timestamp', e);
      }
    }
    
    return {
      id: rawMessage.id,
      chatRoomId: rawMessage.chatRoomId,
      senderId: rawMessage.senderId,
      senderName: rawMessage.senderName,
      senderProfilePic: rawMessage.senderProfilePic,
      content: rawMessage.content,
      timestamp: timestamp,
      read: rawMessage.read,
      media: rawMessage.attachments ? rawMessage.attachments.map((attachment: any) => ({
        id: attachment.id || '',
        url: attachment.url || '',
        contentType: attachment.mimeType || attachment.type || 'application/octet-stream',
        fileName: attachment.name || 'file',
        size: attachment.size || 0
      })) : []
    };
  }
  
  public setupReconnection() {
    if (!StompClientSingleton._client) return;
    
    // Check connection status every 5 seconds
    const reconnectInterval = setInterval(() => {
      if (!StompClientSingleton._client || !StompClientSingleton._client.connected) {
        console.log('WebSocket disconnected, attempting to reconnect');
        this.connect(getAccessToken(), this.connectedCallback || undefined);
      }
    }, 5000);
    
    // Store the interval ID to clear it when needed
    this.reconnectIntervalId = reconnectInterval;
    
    return () => {
      if (this.reconnectIntervalId) {
        clearInterval(this.reconnectIntervalId);
      }
    };
  }
  
  // Add this helper method to register callbacks
  private registerRoomCallback(chatRoomId: string, callback: (message: ChatMessage) => void) {
    // Get existing callbacks or create new array
    const callbacks = this.messageCallbacks.get(chatRoomId) || [];
    
    // Add this callback if it's not already registered
    if (!callbacks.includes(callback)) {
      this.messageCallbacks.set(chatRoomId, [...callbacks, callback]);
    }
  }
  
  public getMessageCallbacks(chatRoomId: string): ((message: ChatMessage) => void)[] | undefined {
    return this.messageCallbacks.get(chatRoomId);
  }
  
  public addMessageCallback(chatRoomId: string, callback: (message: ChatMessage) => void): void {
    const callbacks = this.messageCallbacks.get(chatRoomId) || [];
    if (!callbacks.includes(callback)) {
      this.messageCallbacks.set(chatRoomId, [...callbacks, callback]);
    }
  }
}

class ChatService {
  private stompClient = StompClientSingleton.getInstance();
  private roomTopicSubscriptions = new Map<string, any>();
  private heartbeatInterval: NodeJS.Timeout | null = null;
  
  // Add a cache for chat rooms to avoid repeated API calls
  private roomsCache: { 
    data: ChatRoom[] | null,
    timestamp: number
  } = {
    data: null,
    timestamp: 0
  };
  
  // Initialize the WebSocket connection
  public initializeWebSocket(token: string, onConnected?: () => void) {
    if (USE_WEBSOCKETS) {
      this.stompClient.connect(token, onConnected);
    } else {
      console.log('WebSockets disabled, using REST API only');
      if (onConnected) onConnected();
    }
  }
  
  // Get all chat rooms for the current user
  public async getUserChatRooms(): Promise<ChatRoom[] | { content: ChatRoom[] }> {
    try {
      console.log("ChatService: Fetching user chat rooms");
      
      // Check cache first (valid for 10 seconds)
      const now = Date.now();
      const cacheAge = now - this.roomsCache.timestamp;
      if (this.roomsCache.data && cacheAge < 10000) {
        console.log("ChatService: Using cached chat rooms, age:", Math.round(cacheAge/1000), "s");
        return this.roomsCache.data;
      }
      
      const token = getAccessToken();
      
      if (!token) {
        console.error("ChatService: No token available for chat rooms request");
        throw new Error("No authentication token available");
      }
      
      console.log("ChatService: Making rooms API request...");
      const response = await authFetch('/rooms');
      
      if (!response.ok) {
        console.error(`ChatService: Server returned ${response.status} when fetching chat rooms`);
        throw new Error(`Server returned ${response.status}`);
      }
      
      const data = await response.json();
      console.log("ChatService: Raw chat rooms response:", data);
      
      // Handle different response formats and cache the result
      if (Array.isArray(data)) {
        console.log("ChatService: Returning array of chat rooms:", data.length);
        this.roomsCache = {
          data,
          timestamp: now
        };
        return data;
      } else if (data && typeof data === 'object' && 'content' in data) {
        console.log("ChatService: Returning content property with chat rooms:", data.content?.length);
        this.roomsCache = {
          data: data.content,
          timestamp: now
        };
        return data;
      } else {
        console.error("ChatService: Unexpected response format:", data);
        return { content: [] };
      }
    } catch (error) {
      console.error("ChatService: Error fetching chat rooms:", error);
      return { content: [] };
    }
  }
  
  // Get details for a specific chat room
  public async getChatRoomDetails(chatRoomId: string): Promise<ChatRoom> {
    try {
      // Check if this room is in our cache
      if (this.roomsCache.data) {
        const cachedRoom = this.roomsCache.data.find(room => room.id === chatRoomId);
        if (cachedRoom) {
          console.log("ChatService: Using cached room details");
          return cachedRoom;
        }
      }
      
      const response = await authFetch(`/rooms/${chatRoomId}`);
      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }
      return response.json();
    } catch (error) {
      console.error("ChatService: Error fetching room details:", error);
      throw error;
    }
  }
  
  // Cache for chat rooms by user ID
  private userRoomCache = new Map<string, { room: ChatRoom; timestamp: number }>();
  
  // Create or get a chat room with another user
  public async createOrGetChatRoom(userId: string): Promise<ChatRoom> {
    try {
      console.log(`ChatService: Creating or getting chat room with user ${userId}`);
      
      // Check cache first (valid for 30 seconds)
      const now = Date.now();
      const cachedData = this.userRoomCache.get(userId);
      if (cachedData && (now - cachedData.timestamp < 30000)) {
        console.log("ChatService: Using cached chat room");
        return cachedData.room;
      }
      
      const token = getAccessToken();
      if (!token) {
        console.error('ChatService: No token available for creating chat room');
        throw new Error('No authentication token available');
      }
      
      const response = await authFetch(`/rooms/user/${userId}`, {
        method: 'POST'
      });
      
      if (!response.ok) {
        console.error(`ChatService: Server returned ${response.status} when creating chat room`);
        throw new Error(`Server returned ${response.status}`);
      }
      
      const room = await response.json();
      console.log('ChatService: Chat room response:', room);
      
      // Cache the result
      this.userRoomCache.set(userId, { room, timestamp: now });
      
      return room;
    } catch (error) {
      console.error('ChatService: Error creating or getting chat room:', error);
      throw error;
    }
  }
  
  // Get messages for a chat room with pagination
  public async getChatMessages(chatRoomId: string, page = 0, size = 20): Promise<{
    content: ChatMessage[],
    totalPages: number,
    totalElements: number
  }> {
    try {
      const response = await authFetch(
        `/rooms/${chatRoomId}/messages?page=${page}&size=${size}&sort=timestamp,desc`
      );
      
      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }
      
      const data = await response.json();
      
      // Convert each message to our frontend format
      const convertedMessages = data.content.map((msg: any) => this.stompClient.convertMessageFormat(msg));
      
      return {
        content: convertedMessages,
        totalPages: data.totalPages,
        totalElements: data.totalElements
      };
    } catch (error) {
      console.error('Error fetching messages:', error);
      return {
        content: [],
        totalPages: 0,
        totalElements: 0
      };
    }
  }
  
  // Send a message via REST API
  public async sendMessage(
    chatRoomId: string, 
    content: string,
    mediaIds: string[] = []
  ): Promise<ChatMessage> {
    const response = await authFetch('/messages', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        chatRoomId,
        content,
        mediaIds
      })
    });
    return response.json();
  }
  
  // Send a message via WebSocket with better fallback
  public async sendMessageWebSocket(
    chatRoomId: string, 
    content: string,
    mediaIds: string[] = []
  ): Promise<ChatMessage | void> {
    if (USE_WEBSOCKETS) {
      const client = this.stompClient.getClient();
      
      if (client && client.connected) {
        try {
          console.log('🟢 Sending message via WebSocket');
          
          // Create a unique temp ID for optimistic UI updates
          const tempId = `temp-${Date.now()}`;
          
          // Create a temporary message for the UI
          const tempMessage: ChatMessage = {
            id: tempId,
            chatRoomId,
            senderId: localStorage.getItem('userId') || '',
            content,
            timestamp: new Date().toISOString(),
            read: false,
            media: mediaIds.length > 0 ? mediaIds.map(id => ({
              id,
              url: '',
              contentType: 'pending',
              fileName: 'Uploading...',
              size: 0
            })) : []
          };
          
          // Use the correct destination - this must match your backend
          const destination = '/app/chat.sendMessage';
          
          const payload = {
            chatRoomId,
            content,
            mediaIds
          };
          
          console.log('🟢 Sending to:', destination);
          console.log('🟢 Payload:', payload);
          
          // Send the message
          client.publish({
            destination: destination,
            body: JSON.stringify(payload),
            headers: {
              'Authorization': `Bearer ${getAccessToken()}`
            }
          });
          
          console.log('🟢 WebSocket message sent');
          
          return tempMessage;
        } catch (error) {
          console.error('❌ WebSocket send failed:', error);
          return this.sendMessage(chatRoomId, content, mediaIds);
        }
      } else {
        console.log('⚠️ WebSocket not connected, using REST API');
        return this.sendMessage(chatRoomId, content, mediaIds);
      }
    } else {
      return this.sendMessage(chatRoomId, content, mediaIds);
    }
  }
  
  // Mark messages in a chat room as read
  public async markMessagesAsRead(chatRoomId: string): Promise<void> {
    await authFetch(`/rooms/${chatRoomId}/read`, {
      method: 'POST'
    });
  }
  
  // Subscribe to messages from a specific chat room
  public subscribeToRoom(chatRoomId: string, callback: (message: ChatMessage) => void) {
    console.log(`🔔 Subscribing to room ${chatRoomId}`);
    
    // Make sure WebSocket is connected
    const client = this.stompClient.getClient();
    if (!client || !client.connected) {
      console.log('❗ WebSocket not connected, connecting now...');
      const token = getAccessToken();
      this.initializeWebSocket(token, () => {
        console.log('🔔 Connected, now subscribing to room');
        this.registerRoomCallback(chatRoomId, callback);
        
        // Also subscribe to the chat room topic directly
        if (client) {
          const subscription = client.subscribe(`/topic/chat/${chatRoomId}`, (message) => {
            try {
              const messageData = JSON.parse(message.body);
              console.log('🔔 Received message via room topic:', messageData);
              
              // Convert to our format and call the callback
              const chatMessage = this.stompClient.convertMessageFormat(messageData);
              callback(chatMessage);
            } catch (error) {
              console.error('❌ Error processing topic message:', error);
            }
          });
          
          // Store this subscription
          this.roomTopicSubscriptions.set(chatRoomId, subscription);
        }
      });
      return;
    }
    
    // Subscribe via the singleton
    this.registerRoomCallback(chatRoomId, callback);
    
    // Also subscribe to the chat room topic directly
    if (client) {
      const subscription = client.subscribe(`/topic/chat/${chatRoomId}`, (message) => {
        try {
          const messageData = JSON.parse(message.body);
          console.log('🔔 Received message via room topic:', messageData);
          
          // Convert to our format and call the callback
          const chatMessage = this.stompClient.convertMessageFormat(messageData);
          callback(chatMessage);
        } catch (error) {
          console.error('❌ Error processing topic message:', error);
        }
      });
      
      // Store this subscription
      this.roomTopicSubscriptions.set(chatRoomId, subscription);
    }
  }
  
  // Unsubscribe from messages from a specific chat room
  public unsubscribeFromRoom(chatRoomId: string, callback: (message: ChatMessage) => void) {
    this.stompClient.unsubscribeFromRoom(chatRoomId, callback);
  }
  
  // Disconnect WebSocket
  public disconnect() {
    this.stompClient.disconnect();
  }
  
  public async uploadMedia(files: File[]): Promise<MediaItem[]> {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    
    console.log('Uploading media files:', files);
    
    const response = await fetch(`${API_BASE_URL}/messages/media`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${getAccessToken()}`
      },
      body: formData
    });
    
    if (!response.ok) {
      console.error('Media upload failed:', response.status, response.statusText);
      throw new Error('Failed to upload media');
    }
    
    const uploadedFiles = await response.json();
    console.log('Media upload successful:', uploadedFiles);
    return uploadedFiles;
  }
  
  // Add this method to ChatService class
  public setupReconnection() {
    return this.stompClient.setupReconnection();
  }
  
  // Add this method to get the STOMP client
  public getStompClient() {
    return this.stompClient.getClient();
  }
  
  // Add this method to check WebSocket status
  public checkWebSocketStatus(): { connected: boolean, client: any } {
    const client = this.stompClient.getClient();
    return {
      connected: !!(client && client.connected),
      client: client
    };
  }
  
  // Add this method to the ChatService class
  public debugWebSocketConnection() {
    const client = this.stompClient.getClient();
    if (!client || !client.connected) {
      console.error('WebSocket not connected');
      return 'WebSocket not connected';
    }
    
    // Send a test message to the public topic
    client.publish({
      destination: '/topic/public',
      body: JSON.stringify({
        type: 'DEBUG',
        content: 'Test message',
        timestamp: new Date().toISOString()
      })
    });
    
    console.log('Debug message sent to public topic');
    return 'Debug message sent';
  }
  
  // Add a heartbeat check to ensure the WebSocket stays connected
  public setupHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }
    
    // Check connection every 30 seconds
    this.heartbeatInterval = setInterval(() => {
      const client = this.stompClient.getClient();
      if (!client || !client.connected) {
        console.log('❗ WebSocket disconnected, reconnecting...');
        this.initializeWebSocket(getAccessToken());
      } else {
        // Send a heartbeat message to keep the connection alive
        client.publish({
          destination: '/app/heartbeat',
          body: JSON.stringify({ timestamp: new Date().toISOString() }),
          headers: { 'Authorization': `Bearer ${getAccessToken()}` }
        });
        console.log('💓 Heartbeat sent');
      }
    }, 30000);
    
    return () => {
      if (this.heartbeatInterval) {
        clearInterval(this.heartbeatInterval);
      }
    };
  }
  
  // Add this helper method to register callbacks
  private registerRoomCallback(chatRoomId: string, callback: (message: ChatMessage) => void) {
    this.stompClient.addMessageCallback(chatRoomId, callback);
  }
}

export default new ChatService();
